import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Wrench, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "ACCOUNTS"];
const OWNERSHIP = ["OWNED", "HIRED"];

export default function Equipment() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/equipment");
      setEquipment(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <PageHeader title="Equipment" description="Plant & machinery master. Hourly rate drives logbook cost entries; fuel cost/litre is used when hours aren't logged." action={canWrite && <CreateEquipmentDialog onCreated={load} />} />

      <div className="rounded-xl border border-border bg-card">
        {!loading && equipment.length === 0 ? (
          <EmptyState title="No equipment yet" testId="equipment-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Ownership</TableHead><TableHead className="text-right">Hourly rate</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {equipment.map((eq) => (
                <TableRow key={eq.id} data-testid={`equipment-row-${eq.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{eq.code}</TableCell>
                  <TableCell className="text-foreground">{eq.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{eq.type}</TableCell>
                  <TableCell><Badge variant={eq.ownership === "OWNED" ? "default" : "secondary"}>{eq.ownership}</Badge></TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(eq.hourly_rate, 2)}</TableCell>
                  <TableCell className="text-right"><EquipmentToolsDialog equipment={eq} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function EquipmentToolsDialog({ equipment }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("breakdown");
  const [breakdowns, setBreakdowns] = useState([]);
  const [form, setForm] = useState({ breakdown_date: todayISO(), reason: "", downtime_hours: "", repair_cost: "" });
  const [range, setRange] = useState({ date_from: todayISO(), date_to: todayISO() });
  const [util, setUtil] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadBreakdowns = async () => {
    const { data } = await api.get(`/equipment/${equipment.id}/breakdowns`);
    setBreakdowns(data);
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) loadBreakdowns();
  };

  const logBreakdown = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/breakdowns", { equipment_id: equipment.id, ...form, repair_cost: form.repair_cost || null });
      toast.success("Breakdown logged");
      setForm({ breakdown_date: todayISO(), reason: "", downtime_hours: "", repair_cost: "" });
      loadBreakdowns();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const resolve = async (breakdown) => {
    try {
      await api.post(`/breakdowns/${breakdown.id}/resolve`, { resolved_date: todayISO() });
      toast.success("Marked resolved");
      loadBreakdowns();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const runUtilisation = async () => {
    try {
      const { data } = await api.get(`/equipment/${equipment.id}/utilisation`, { params: range });
      setUtil(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`equipment-tools-button-${equipment.code}`}><Wrench className="h-3.5 w-3.5" /> Breakdowns & Utilisation</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid={`equipment-tools-dialog-${equipment.code}`}>
        <DialogHeader><DialogTitle>{equipment.code} — {equipment.name}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5 border-b border-border pb-2">
          <Button size="sm" variant={tab === "breakdown" ? "default" : "ghost"} onClick={() => setTab("breakdown")} data-testid="equipment-tab-breakdown">Breakdowns</Button>
          <Button size="sm" variant={tab === "util" ? "default" : "ghost"} onClick={() => setTab("util")} data-testid="equipment-tab-utilisation"><Gauge className="h-3.5 w-3.5" /> Utilisation</Button>
        </div>
        {tab === "breakdown" && (
          <div className="space-y-4">
            <form onSubmit={logBreakdown} className="space-y-3" data-testid="log-breakdown-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input required type="date" value={form.breakdown_date} onChange={(e) => setForm({ ...form, breakdown_date: e.target.value })} className="mt-1.5" data-testid="breakdown-date-input" /></div>
                <div><Label>Downtime (hrs)</Label><Input required type="number" step="0.5" value={form.downtime_hours} onChange={(e) => setForm({ ...form, downtime_hours: e.target.value })} className="mt-1.5" data-testid="breakdown-downtime-input" /></div>
              </div>
              <div><Label>Reason</Label><Input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" data-testid="breakdown-reason-input" /></div>
              <div><Label>Repair cost (optional)</Label><Input type="number" step="0.01" value={form.repair_cost} onChange={(e) => setForm({ ...form, repair_cost: e.target.value })} className="mt-1.5" data-testid="breakdown-repair-cost-input" /></div>
              <Button type="submit" size="sm" disabled={saving} data-testid="log-breakdown-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Log breakdown</Button>
            </form>
            <div className="space-y-1.5" data-testid="breakdown-history-list">
              {breakdowns.length === 0 ? <p className="text-xs text-muted-foreground">No breakdowns logged.</p> : breakdowns.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-border p-2 text-xs">
                  <span className="text-muted-foreground">{b.breakdown_date} · {b.reason} ({b.downtime_hours}h)</span>
                  {b.resolved_date ? <Badge variant="secondary">Resolved</Badge> : (
                    <Button size="sm" variant="ghost" onClick={() => resolve(b)} data-testid={`resolve-breakdown-button-${b.id}`}>Resolve</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "util" && (
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div><Label className="text-[11px]">From</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1" data-testid="utilisation-date-from-input" /></div>
              <div><Label className="text-[11px]">To</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1" data-testid="utilisation-date-to-input" /></div>
              <Button size="sm" onClick={runUtilisation} data-testid="run-utilisation-button">Run</Button>
            </div>
            {util && (
              <div className="grid grid-cols-2 gap-2 text-sm" data-testid="utilisation-result">
                <div className="rounded-lg border border-border p-2.5"><p className="text-[11px] text-muted-foreground">Available</p><p className="font-mono font-semibold text-foreground">{util.available_hours}h</p></div>
                <div className="rounded-lg border border-border p-2.5"><p className="text-[11px] text-muted-foreground">Run</p><p className="font-mono font-semibold text-emerald-400">{util.run_hours}h</p></div>
                <div className="rounded-lg border border-border p-2.5"><p className="text-[11px] text-muted-foreground">Breakdown</p><p className="font-mono font-semibold text-rose-400">{util.breakdown_hours}h</p></div>
                <div className="rounded-lg border border-border p-2.5"><p className="text-[11px] text-muted-foreground">Idle</p><p className="font-mono font-semibold text-muted-foreground">{util.idle_hours}h</p></div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateEquipmentDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "", make: "", model: "", registration_no: "", ownership: "OWNED", hourly_rate: "0", fuel_type: "", fuel_cost_per_litre: "0" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/equipment", form);
      toast.success(`Equipment ${form.code} created`);
      setForm({ code: "", name: "", type: "", make: "", model: "", registration_no: "", ownership: "OWNED", hourly_rate: "0", fuel_type: "", fuel_cost_per_litre: "0" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-equipment-button"><Plus className="h-4 w-4" /> New Equipment</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Equipment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-equipment-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required value={form.code} onChange={set("code")} className="mt-1.5" data-testid="equipment-code-input" /></div>
            <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="equipment-name-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><Input required value={form.type} onChange={set("type")} placeholder="Excavator, Crane, Dozer…" className="mt-1.5" data-testid="equipment-type-input" /></div>
            <div>
              <Label>Ownership</Label>
              <Select value={form.ownership} onValueChange={(v) => setForm({ ...form, ownership: v })}>
                <SelectTrigger className="mt-1.5" data-testid="equipment-ownership-select"><SelectValue /></SelectTrigger>
                <SelectContent>{OWNERSHIP.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Make</Label><Input value={form.make} onChange={set("make")} className="mt-1.5" data-testid="equipment-make-input" /></div>
            <div><Label>Model</Label><Input value={form.model} onChange={set("model")} className="mt-1.5" data-testid="equipment-model-input" /></div>
          </div>
          <div><Label>Registration no.</Label><Input value={form.registration_no} onChange={set("registration_no")} className="mt-1.5" data-testid="equipment-registration-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Hourly rate</Label><Input type="number" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} className="mt-1.5" data-testid="equipment-hourly-rate-input" /></div>
            <div><Label>Fuel type</Label><Input value={form.fuel_type} onChange={set("fuel_type")} className="mt-1.5" data-testid="equipment-fuel-type-input" /></div>
            <div><Label>Fuel cost/litre</Label><Input type="number" step="0.01" value={form.fuel_cost_per_litre} onChange={set("fuel_cost_per_litre")} className="mt-1.5" data-testid="equipment-fuel-cost-input" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-equipment-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
